import { queries } from "@/graphql";
import type { ApolloClient } from "@apollo/client";
import { sendSlackLogin, setCacheWithExpiry } from "@/utils";

type UpdateProfileFn =
  | ((args: {
      variables: { currentRoleKey?: string | null };
    }) => Promise<unknown> | unknown)
  | null;

type UserWithRole = {
  id?: string | null;
  name?: string | null;
  email?: string | null;
  currentTeam?: string | null;
  currentCustomerKey?: string | null;
  currentSubsidiaryKey?: string | null;
  currentRoleKey?: string | null;
  roles?: Array<string | null> | null;
  [key: string]: unknown;
};

type ApolloClientLike = Pick<ApolloClient, "query"> | null;

type SetUserFn = ((user: UserWithRole) => void) | null;

export const fillUserWithMe = async (
  me: UserWithRole,
  apolloClient: ApolloClientLike,
  setUser: SetUserFn = null,
  updateProfile: UpdateProfileFn = null,
  isLogin = false,
) => {
  let finalMe = me;
  let roleKey = null;
  let shouldUpdate = false;

  // First check if currentRoleKey exists
  if (!finalMe?.currentRoleKey) {
    // Try to get from roles array
    if (finalMe?.roles?.length && finalMe.roles[0]) {
      roleKey = finalMe.roles[0];
    } else {
      // If roles are empty, try to get from partners
      try {
        if (apolloClient && me?.email) {
          const { data: partnersData } = await apolloClient.query({
            query: queries.getAllPartners,
            variables: { email: me.email },
          });

          const partners = (partnersData as any)?.getPartners || [];
          if (partners.length > 0 && partners[0]?.users?.length > 0) {
            roleKey = partners[0].users[0].role;
          }

          // If still no role found, try tenants
          if (!roleKey) {
            const { data: tenantsData } = await apolloClient.query({
              query: queries.tenantsList,
              variables: { email: me.email },
            });

            const tenants = (tenantsData as any)?.tenantsList || [];
            if (tenants.length > 0 && tenants[0]?.users?.length > 0) {
              roleKey = tenants[0].users[0].role;
            }
          }
        }
      } catch {
        // Silently fail and continue without role from partners/tenants
      }
    }

    if (roleKey) {
      finalMe = {
        ...me,
        currentRoleKey: roleKey,
      };
      // Mark that we need to update if currentRoleKey changed
      if (me?.currentRoleKey !== roleKey) {
        shouldUpdate = true;
      }
    }
  }

  try {
    if (apolloClient && me?.email) {
      const currentTenantId = me?.currentTenantId || "";
      const { data: tenantsData } = await apolloClient.query({
        query: queries.tenantsList,
        variables: { email: me.email },
      });

      const tenantsList = (tenantsData as any)?.tenantsList || [];

      const customerVariables = {
        tenantId:
          currentTenantId ||
          tenantsList[0]?.id ||
          tenantsList[0]?.Id ||
          "--none--",
      };

      const { data: customers } = await apolloClient.query({
        query: queries.customers,
        variables: customerVariables,
      });

      const customerList = (customers as any)?.getCustomers || [];
      const currentCustKey = finalMe?.currentCustomerKey;
      let matchedCust = null;
      if (currentCustKey) {
        matchedCust = customerList.find(
          (c: any) =>
            c.key === currentCustKey ||
            (c.country &&
              c.cvr &&
              `${c.country}${c.cvr}`.toUpperCase() ===
                String(currentCustKey).toUpperCase()) ||
            c.id === currentCustKey ||
            c.customerKey === currentCustKey,
        );
      }
      if (!matchedCust && customerList.length > 0) {
        matchedCust = customerList[0];
      }

      finalMe = {
        ...finalMe,
        hasCompanies: !!customerList.length,
        ...(matchedCust
          ? {
              currentCustomerKey:
                currentCustKey ||
                matchedCust.key ||
                (matchedCust.country && matchedCust.cvr
                  ? `${matchedCust.country.toUpperCase()}${matchedCust.cvr}`
                  : matchedCust.customerKey) ||
                matchedCust.id,
              currentTeam: finalMe.currentTeam || matchedCust.name,
              currentTeamId: finalMe.currentTeamId || matchedCust.id,
            }
          : {}),
        ...(finalMe.currentSubsidiaryKey === "all"
          ? {
              currentSubsidiary: finalMe.currentSubsidiary || "All",
              currentSubsidiaryId: finalMe.currentSubsidiaryId || "all",
            }
          : {}),
      };
    }
  } catch {
    // Silently fail and continue without role from partners/tenants
  }

  // Save to database if currentRoleKey changed
  if (shouldUpdate && updateProfile && setUser) {
    try {
      await updateProfile({
        variables: {
          currentRoleKey: finalMe.currentRoleKey,
        },
      });
      // Update user state
      setUser(finalMe);
    } catch {
      // Silently fail if update fails, but continue with finalMe
    }
  }

  try {
    const key = "me";
    const cacheTTL = 3600; // 3600 = 1 hour
    setCacheWithExpiry(key, finalMe, cacheTTL);

    // Send Slack login message only on actual login, not on every page load
    if (isLogin) {
      sendSlackLogin(finalMe);
    }

    // dispatch({ type: 'auth/addUser', payload: finalMe });

    return finalMe;
  } catch {
    // sendSlackDebug("comeMe onComplete catch");
    // unsetToken();
  }
};
